import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BarChart3, Timer, TrendingUp, AlertTriangle, Users, Search, Trophy } from 'lucide-react';
import { dashboardPerformance } from '../api/resources';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import DateInput from '../components/ui/DateInput';
import { inr, fmtDate } from '../utils/format';

/**
 * How the workshop is doing: production time, on-time record, what is late,
 * where orders pile up, and what each worker is carrying.
 *
 * Its own page, reached from Workers — the dashboard is for today's takings,
 * this is for the owner asking why things take as long as they do.
 */
export default function Performance() {
  // Wide while you are here, back to normal on the way out.
  const shell = useOutletContext();
  useEffect(() => {
    shell?.setWide?.(true);
    return () => shell?.setWide?.(false);
  }, [shell]);

  const nav = useNavigate();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });

  return (
    <div className="space-y-5">
      <PageHeader
        icon={BarChart3}
        title="Workshop Performance"
        subtitle="Production time, delays, and what each worker is carrying"
        actions={(
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-content-subtle">From</span>
              <DateInput value={from} onChange={setFrom} className="input-sm w-32" />
            </label>
            <label className="block">
              <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-content-subtle">To</span>
              <DateInput value={to} onChange={setTo} className="input-sm w-32" />
            </label>
            <button className="btn-primary" onClick={() => setRange({ from, to })} disabled={!from && !to}>Apply</button>
            {(range.from || range.to) && (
              <button className="btn-outline" onClick={() => { setFrom(''); setTo(''); setRange({ from: '', to: '' }); }}>All time</button>
            )}
            <button className="btn-outline" onClick={() => nav('/workers')}><ArrowLeft size={15} /> Workers</button>
          </div>
        )}
      />
      <PerformancePanel from={range.from} to={range.to} />
    </div>
  );
}

const LOAD_DOT = { free: 'bg-success-500', ok: 'bg-warning-500', busy: 'bg-danger-500' };
const LOAD_WORD = { free: 'Free', ok: 'Steady', busy: 'Heavy' };
const ROLE_WORD = { CUTTING_MASTER: 'Cutting master', SEWING_TAILOR: 'Tailor', ALL_ROUNDER: 'All-rounder', NO_TRADE: 'Other' };

const FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'busy', label: 'Heavy load' },
  { key: 'late', label: 'Has late work' },
  { key: 'free', label: 'Free' },
  { key: 'ontime', label: 'Best record' },
];

/**
 * Who is carrying what — searchable, sortable, and filterable, because the
 * owner comes here with a question ("who can take this?", "who is behind?")
 * rather than to read a list top to bottom.
 */
function WorkerCapacity({ workers }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'pieces', dir: 'desc' });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = workers.filter((w) => {
      if (needle && !`${w.name} ${ROLE_WORD[w.role] || w.role}`.toLowerCase().includes(needle)) return false;
      if (filter === 'busy') return w.load === 'busy';
      if (filter === 'late') return w.overdue > 0;
      if (filter === 'free') return w.load === 'free';
      if (filter === 'ontime') return w.onTimePct != null && w.onTimePct >= 85;
      return true;
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      if (typeof av === 'string' || typeof bv === 'string') return String(av || '').localeCompare(String(bv || '')) * dir;
      return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
    });
    return list;
  }, [workers, q, filter, sort]);

  const total = rows.reduce((acc, w) => ({
    openJobs: acc.openJobs + w.openJobs,
    pieces: acc.pieces + w.pieces,
    dueThisWeek: acc.dueThisWeek + w.dueThisWeek,
    overdue: acc.overdue + w.overdue,
    doneOrders: acc.doneOrders + (w.doneOrders || 0),
    unpaid: acc.unpaid + Number(w.unpaid || 0),
  }), { openJobs: 0, pieces: 0, dueThisWeek: 0, overdue: 0, doneOrders: 0, unpaid: 0 });

  const th = (key, label, align = 'left') => {
    const on = sort.key === key;
    return (
      <th
        className={`th cursor-pointer select-none ${align === 'right' ? 'text-right' : ''} ${on ? 'text-brand-700' : ''}`}
        title="Sort by this column"
        onClick={() => setSort((sIn) => ({ key, dir: sIn.key === key && sIn.dir === 'desc' ? 'asc' : 'desc' }))}
      >
        {label}{on ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
      </th>
    );
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2.5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-content">Worker capacity</h3>
        <div className="relative ml-2 min-w-[11rem]">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input
            className="input input-sm pl-7"
            placeholder="Find a worker…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex rounded-lg border border-line p-0.5">
          {FILTERS.map((ff) => (
            <button
              key={ff.key}
              type="button"
              onClick={() => setFilter(ff.key)}
              className={`rounded px-2 py-1 text-2xs font-semibold ${filter === ff.key ? 'bg-brand-600 text-white' : 'text-content-muted hover:text-content'}`}
            >
              {ff.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-2xs text-content-subtle">
          {rows.length} of {workers.length} · click a row for the full record
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm italic text-content-subtle">
          {workers.length ? 'No worker matches that.' : 'No active workers.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {th('name', 'Worker')}
                {th('role', 'Trade')}
                {th('openJobs', 'Jobs', 'right')}
                {th('inProgress', 'Working now', 'right')}
                {th('pieces', 'Pieces', 'right')}
                {th('dueThisWeek', 'Due this week', 'right')}
                {th('overdue', 'Late', 'right')}
                {th('doneOrders', 'Orders done', 'right')}
                {th('onTimePct', 'On time', 'right')}
                {th('lateJobs', 'Missed date', 'right')}
                {th('avgDays', 'Takes about', 'right')}
                {th('nextDue', 'Next delivery', 'left')}
                {th('committed', 'Pay in hand', 'right')}
                {th('unpaid', 'Unpaid', 'right')}
                {th('finished', 'Finished (7d)', 'right')}
                {th('load', 'Load')}
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr
                  key={w.id}
                  className="cursor-pointer border-t border-line hover:bg-surface-hover"
                  onClick={() => nav(`/workers/${w.id}`, { state: { from: '/performance' } })}
                >
                  <td className="px-4 py-2 font-medium text-content">{w.name}</td>
                  <td className="px-4 py-2 text-content-muted">{ROLE_WORD[w.role] || w.role}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.openJobs}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.inProgress}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.pieces}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{w.dueThisWeek || '—'}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${w.overdue ? 'font-bold text-danger-600' : 'text-content-subtle'}`}>{w.overdue || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{w.doneOrders || '—'}</td>
                  <td className={`px-4 py-2 text-right font-semibold tabular-nums ${
                    w.onTimePct == null ? 'text-content-subtle'
                      : w.onTimePct >= 85 ? 'text-success-700'
                        : w.onTimePct >= 60 ? 'text-warning-700' : 'text-danger-600'
                  }`}>{w.onTimePct != null ? `${w.onTimePct}%` : '—'}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${w.lateJobs ? 'text-danger-600' : 'text-content-subtle'}`}>{w.lateJobs || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-content-muted">{w.avgDays != null ? `${w.avgDays}d` : '—'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-content-muted">{w.nextDue ? fmtDate(w.nextDue) : '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-content-muted">{inr(w.committed || 0)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${w.unpaid ? 'font-semibold text-danger-600' : 'text-content-subtle'}`}>{inr(w.unpaid || 0)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-content-muted">{w.finished || '—'}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`h-2 w-2 rounded-full ${LOAD_DOT[w.load]}`} /> {LOAD_WORD[w.load]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-line bg-surface-sunken text-xs font-semibold">
                <td className="px-4 py-2" colSpan={2}>Total ({rows.length})</td>
                <td className="px-4 py-2 text-right tabular-nums">{total.openJobs}</td>
                <td className="px-4 py-2" />
                <td className="px-4 py-2 text-right tabular-nums">{total.pieces}</td>
                <td className="px-4 py-2 text-right tabular-nums">{total.dueThisWeek || '—'}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${total.overdue ? 'text-danger-600' : ''}`}>{total.overdue || '—'}</td>
                <td className="px-4 py-2 text-right tabular-nums">{total.doneOrders || '—'}</td>
                <td className="px-4 py-2" colSpan={5} />
                <td className="px-4 py-2 text-right tabular-nums">{inr(total.unpaid)}</td>
                <td className="px-4 py-2" colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'plain' }) {
  const tones = { plain: 'text-content', good: 'text-success-700', bad: 'text-danger-600' };
  return (
    <div>
      <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">{label}</p>
      <p className={`text-base font-bold tabular-nums ${tones[tone]}`}>{value}</p>
    </div>
  );
}

function Kpi({ label, value, sub, tone = 'plain', icon: Icon }) {

  const tones = { plain: 'text-content', good: 'text-success-700', warn: 'text-warning-700', bad: 'text-danger-600' };
  return (
    <div className="card p-4">
      <p className="flex items-center gap-1.5 text-2xs font-bold uppercase tracking-wider text-content-subtle">
        {Icon && <Icon size={13} />} {label}
      </p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-content-muted">{sub}</p>}
    </div>
  );
}

function PerformancePanel({ from, to }) {
  const nav = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dash-performance', from, to],
    queryFn: () => dashboardPerformance({ from: from || undefined, to: to || undefined }),
  });

  if (isLoading) return <Spinner label="Measuring the workshop…" />;
  if (isError || !data) {
    return <div className="card p-10 text-center text-sm text-content-muted">Could not load the performance figures.</div>;
  }

  const { totals, stages = [], workers = [], late = [], topWorker } = data;
  const prod = totals.production;
  const onTime = totals.onTime;
  const peak = Math.max(1, ...stages.map((s) => s.orders));

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={Timer}
          label="Average production time"
          value={prod.avgDays != null ? `${prod.avgDays} days` : '—'}
          sub={prod.sample ? `Typical ${prod.medianDays} days · from ${prod.sample} finished orders` : 'No finished orders yet'}
        />
        <Kpi
          icon={TrendingUp}
          label="Finished on time"
          value={onTime.pct != null ? `${onTime.pct}%` : '—'}
          tone={onTime.pct == null ? 'plain' : onTime.pct >= 85 ? 'good' : onTime.pct >= 60 ? 'warn' : 'bad'}
          sub={onTime.finished ? `${onTime.onTimeCount} of ${onTime.finished} done before the delivery date` : 'Needs finished work to measure'}
        />
        <Kpi
          icon={AlertTriangle}
          label="Running late"
          value={totals.delayed}
          tone={totals.delayed ? 'bad' : 'good'}
          sub={`${totals.openOrders} order(s) still open in this period`}
        />
        <Kpi
          icon={Users}
          label="Carrying the most"
          value={workers[0]?.pieces ? workers[0].name : '—'}
          sub={workers[0]?.pieces ? `${workers[0].openJobs} job(s) · ${workers[0].pieces} piece(s) on hand` : 'Nobody is carrying work'}
        />
      </div>

      {topWorker && (
        <div className="card flex flex-wrap items-center gap-x-6 gap-y-3 border-l-4 border-l-success-500 p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-success-50 text-success-700">
              <Trophy size={20} />
            </span>
            <div>
              <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">Top worker</p>
              <p className="text-lg font-bold leading-tight text-content">{topWorker.name}</p>
              <p className="text-2xs text-content-muted">{ROLE_WORD[topWorker.role] || topWorker.role}</p>
            </div>
          </div>
          <Stat label="Orders finished" value={topWorker.doneOrders} />
          <Stat label="Jobs finished" value={topWorker.doneJobs} />
          <Stat label="On time" value={topWorker.onTimePct != null ? `${topWorker.onTimePct}%` : '—'} tone="good" />
          <Stat label="Late" value={topWorker.lateJobs || 0} tone={topWorker.lateJobs ? 'bad' : 'plain'} />
          <Stat label="Takes about" value={topWorker.avgDays != null ? `${topWorker.avgDays} days` : '—'} />
          <button
            className="btn-ghost ml-auto"
            onClick={() => nav(`/workers/${topWorker.id}`, { state: { from: '/performance' } })}
          >
            Open their record
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* where orders are sitting */}
        <div className="card p-4">
          <h3 className="mb-1 text-xs font-bold uppercase tracking-wider text-content">Where the open orders are sitting</h3>
          <p className="mb-3 text-2xs text-content-subtle">The longest bar is the stage holding the workshop up.</p>
          <div className="space-y-2.5">
            {stages.map((s) => (
              <div key={s.key}>
                <div className="mb-0.5 flex items-baseline justify-between text-xs">
                  <span className="font-medium text-content">{s.label}</span>
                  <span className="font-bold tabular-nums text-content">{s.orders}</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-surface-sunken">
                  <div
                    className={`h-full rounded-full ${s.orders === peak && s.orders > 0 ? 'bg-danger-500' : 'bg-brand-500'}`}
                    style={{ width: `${Math.round((s.orders / peak) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* late orders */}
        <div className="card p-4">
          <h3 className="mb-1 text-xs font-bold uppercase tracking-wider text-content">Past the delivery date</h3>
          <p className="mb-2 text-2xs text-content-subtle">Worst first — click one to open the order.</p>
          {late.length === 0 ? (
            <p className="py-6 text-center text-sm font-medium text-success-700">Nothing is late.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {late.map((l) => (
                    <tr
                      key={l.id}
                      className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-hover"
                      onClick={() => nav(`/invoices/${l.id}/edit`, { state: { from: '/performance' } })}
                    >
                      <td className="py-1.5 pr-2 font-semibold">{l.orderNo}</td>
                      <td className="py-1.5 pr-2 text-content-muted">{l.customerName}</td>
                      <td className="py-1.5 pr-2 text-content-muted">due {l.deliveryDate}</td>
                      <td className="py-1.5 text-right font-bold text-danger-600">{l.daysLate}d late</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <WorkerCapacity workers={workers} />

      <p className="text-2xs text-content-subtle">
        The app does not record the moment an order is handed to the customer, so “finished”
        here means the last job on it was completed — production time and the on-time figure
        are measured against that.
      </p>
    </div>
  );
}
